import type Logger from 'DTSLib/Logger'

declare const window: {
  onloadTurnstileCallback: () => void
  turnstile?: Turnstile.Turnstile
} & Window

// Warning: we use a custom promise resolver due to a bug related to explicit render setup which triggers
// unwanted errors using turnstile.ready callback. Instead of the window.turnstile.ready(...) use loadPromise.then(...)
// See https://community.cloudflare.com/t/turnstile-fails-to-load-with-explicit-render/660912
let promiseResolver: (value?: unknown) => void
const loadPromise = new Promise((resolve) => {
  promiseResolver = resolve // This is a potential memory leak. Avoid to expose the promise resolver
})
/**
 * Register global "turnstile ready" callback
 */
window.onloadTurnstileCallback = (): void => {
  promiseResolver()
}
/**
 * Logger instance reference, we declare it here because we can't pass it to the web-component constructor
 */
let loggerInstance: Logger | null = null

// Create a class for the element
class TurnstileWidget extends HTMLElement {
  private static observedAttributes = ['sitekey']
  public static tagId = 'turnstileWidgetContainer'
  private widgetId: string | null | undefined

  private readonly node: HTMLElement

  /**
   * Creates an instance of the TurnstileWidget custom element.
   * Initializes the internal container node.
   */
  public constructor() {
    // Always call super first in constructor
    super()
    this.node = containerTag()
  }

  /**
   * Determines whether the Turnstile widget should run in simulated mode.
   *
   * This is based on the `site-key` attribute of the custom element.
   *
   * @returns A boolean indicating if the widget is in simulated mode.
   */
  private getIsSimulatedMode(): boolean {
    return this.getSiteKey() == 'simulated'
  }

  /**
   * Retrieves the site key used to initialize the Turnstile widget.
   *
   * This value is read from the `sitekey` attribute of the custom element.
   * If the attribute is not present, an empty string is returned.
   *
   * @returns The site key string used for Turnstile validation.
   */
  private getSiteKey(): string {
    return this.getAttribute('sitekey') || ''
  }

  /**
   * Dispatches a custom `rendered` event with the provided token.
   *
   * This event can be listened to by parent components to know when the widget
   * has been successfully rendered and validated.
   *
   * @param token - The validation token returned by the Turnstile widget.
   */
  private dispatchRenderedEvent(token: string): void {
    this.dispatchEvent(
      new CustomEvent('turnstile:rendered', {
        bubbles: true,
        cancelable: true,
        detail: { token: token },
      })
    )
  }

  /**
   * Dispatches a custom `on-error` event with the provided error message.
   *
   * This event can be listened to by parent components to know when the widget
   * has encountered an error.
   *
   * @param error - a message
   */
  private dispatchErrorEvent(error: string): void {
    this.dispatchEvent(
      new CustomEvent('turnstile:on-error', {
        bubbles: true,
        cancelable: true,
        detail: { error: error },
      })
    )
  }

  /**
   * Renders the Turnstile widget inside the container node.
   *
   * Initializes the widget with callbacks for success, error, expiration, and timeout events.
   * Store the widget instance ID after rendering.
   */
  private renderTurnstile(): void {
    this.widgetId = window.turnstile?.render(this.node, {
      callback: (token) => {
        this.dispatchRenderedEvent(token)
      },
      'error-callback': (error) => {
        this.dispatchErrorEvent(error)
      },
      // "render" will make the challenge runs automatically after calling the render() function,
      execution: 'render',
      // Default option but let make it explicit. This will re-render automatically a new token when expiration occurs,
      // consider parametrizing this value if a more sophisticated behavior will be needed, a manual re-render in the 'expired-callback'
      // function needs to be added in that case
      'refresh-expired': 'auto',
      // Default value is 8000.
      'retry-interval': 3000,
      sitekey: this.getSiteKey(),
      'timeout-callback': () => {
        this.dispatchErrorEvent('Turnstile timeout occurred')
      },
    })
  }

  /**
   * Lifecycle hook called when the element is added to the DOM.
   * Initializes the Turnstile widget inside the container and sets up event callbacks.
   */
  protected connectedCallback(): void {
    loggerInstance?.devConsoleLog('Turnstile:connectedCallback', 'connected callback')
    this.append(this.node)

    loadPromise.then(() => {
      if (!this.getIsSimulatedMode()) {
        this.renderTurnstile()
      } else {
        // Emits the testing fake token in simulated mode after a short delay
        setTimeout(() => {
          // This is an always valid token that should be verifiable in testing mode
          this.dispatchRenderedEvent('XXXX.DUMMY.TOKEN.XXXX')
        }, 1000)
      }
    })
  }

  /**
   * Lifecycle hook called when the element is removed from the DOM.
   * Cleans up the Turnstile widget instance from the container.
   */
  protected disconnectedCallback(): void {
    loadPromise.then(() => {
      window.turnstile?.remove(this.widgetId || undefined)
    })
  }

  /**
   * Lifecycle hook called when the element is adopted into a new document.
   * Resets the Turnstile widget.
   */
  protected adoptedCallback(): void {
    loadPromise.then(() => {
      window.turnstile?.reset(this.widgetId || undefined)
    })
  }

  /**
   * Lifecycle hook called when one of the element’s attributes is changed.
   *
   * @param name - Name of the attribute that changed
   * @param oldValue - Previous value of the attribute
   * @param newValue - New value of the attribute
   */
  // eslint-disable-next-line class-methods-use-this
  protected attributeChangedCallback(name: unknown, oldValue: unknown, newValue: unknown): void {
    loggerInstance?.devConsoleLog(
      'Turnstile.ts@attributeChangedCallback',
      `Attribute ${name} has changed from ${oldValue} to ${newValue}.`
    )
  }
}

/**
 * Registers the `TurnstileWidget` as a custom HTML element.
 *
 * This allows the widget to be used as a custom tag in HTML documents.
 */
export function register(): void {
  customElements.define('turnstile-widget', TurnstileWidget)
}

type InitArgs = { isPrerenderBrowser: boolean; logger: Logger }

/**
 * Initializes the Turnstile widget integration.
 *
 * Loads the Turnstile script and registers the custom element.
 *
 * This must be called once before using the `<turnstile-widget>` element in your DOM.
 * It ensures the Turnstile script is loaded and the custom element is defined.
 *
 * ## Example
 *
 * ```ts
 * import { init } from './Turnstile'
 * init()
 * window.addEventListener('DOMContentLoaded', () => {
 * const widget = document.createElement('turnstile-widget')
 * widget.setAttribute('sitekey', 'your-site-key-here')
 *
 * widget.addEventListener('rendered', (event) => {
 * const token = (event as CustomEvent).detail.token
 * console.log('CAPTCHA token:', token)
 * })
 *
 * widget.addEventListener('on-error', (event) => {
 * const error = (event as CustomEvent).detail.error
 * console.error('CAPTCHA error:', error)
 * })
 *
 * document.body.appendChild(widget)
 * })
 * ```
 *
 * @param args - initialization arguments
 */
export function init(args: InitArgs): void {
  loggerInstance = args.logger
  // Don't run turnstile in pre-rendering because it will be resolved before `onloadTurnstileCallback` is defined by this module
  if (!args.isPrerenderBrowser) {
    loadTurnstileScript()
  }
  register()
}

/**
 * Dynamically loads the Turnstile script from Cloudflare.
 *
 * Ensures the script is only loaded once by checking for an existing script tag with a specific ID.
 * The script is added with `async` and `defer` attributes for non-blocking load.
 */
function loadTurnstileScript(): void {
  const script = document.createElement('script')
  const tagId = 'turnstileScript'

  script.setAttribute('id', tagId)
  script.setAttribute(
    'src',
    `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback`
  )
  script.setAttribute('defer', 'true')
  script.setAttribute('async', 'true')

  const preRenderedScriptTag = document.getElementById(tagId)

  if (!preRenderedScriptTag) {
    document.head.appendChild(script)
  }
}

/**
 * Creates and returns a container div for the Turnstile widget.
 *
 * Sets a predefined ID on the div so the Turnstile API can render into it.
 *
 * @returns A div element for the Turnstile widget.
 */
function containerTag(): HTMLDivElement {
  const div = document.createElement('div')

  div.setAttribute('id', TurnstileWidget.tagId)

  return div
}
